/* eslint-disable import/no-unassigned-import */
import "swiper/css";
import "swiper/css/a11y";
/* eslint-enable import/no-unassigned-import */
import React from "react";
import { Swiper, type SwiperProps, SwiperSlide } from "swiper/react";
import { Mousewheel, Navigation, A11y } from "swiper";
import { useToken, Box, type BoxProps } from "@chakra-ui/react";
import { uniqueId } from "lodash";
import { SliderNavigationButtons } from "./navigation-buttons";

type SliderProps = {
  children: JSX.Element[];
  controlsMt?: BoxProps["mt"];
} & SwiperProps;

export type LoopedCardProps = {
  tabIndex?: BoxProps["tabIndex"];
};

/**
 * A horizontal slider designed to use full-width on the page, with centered slides and looping content.
 * Supports horizontal scrolling and includes bottom-centered navigation buttons.
 */
export function CenteredLoopSlider({ children, controlsMt = "bigger", ...swiperProps }: SliderProps) {
  const id = uniqueId("full-width-slider-"); // React.useId includes invalid characters for CSS selectors :(
  const previousButtonId = `swiper-navigation-prev-${id}`;
  const nextButtonId = `swiper-navigation-next-${id}`;
  const slideInlinePadding = useToken("space", "smallest");

  return (
    <Box flexDir="column" pos="relative" maxW="100%" gap={controlsMt}>
      <Swiper
        loop
        centeredSlides
        id={id}
        slidesPerView="auto"
        modules={[Navigation, Mousewheel, A11y]}
        navigation={{
          prevEl: `#${previousButtonId}`,
          nextEl: `#${nextButtonId}`,
        }}
        {...swiperProps}
      >
        {children.map((slide) => (
          <SwiperSlide
            key={`${id}-${slide.key}`}
            style={{ width: "auto", height: "auto", paddingInline: slideInlinePadding }}
          >
            {slide}
          </SwiperSlide>
        ))}
      </Swiper>

      <SliderNavigationButtons
        mt={controlsMt}
        justify="center"
        previousButtonId={previousButtonId}
        nextButtonId={nextButtonId}
      />
    </Box>
  );
}
